import { z } from 'zod'
import { ajouterJours } from './calendrier'
import {
  EXECUTANTS_CONSIGNE,
  type ExecutantConsigne,
  OPERATEURS_OBJET,
  type OperateurObjet,
  type StatutTache,
} from './domaine'

// Consignes de tri : la mémoire de l'assistant, écrite ou validée par un humain. Une
// consigne dit, en style télégraphique, quand un mail relève d'un sujet (« quand », lu par
// Claude), ses déclencheurs sûrs (expéditeur, objet : sans modèle), et, en données, ce qui
// en découle : entité, règle d'échéance, bail, exécutant, attente d'un tiers, ignorer le
// mail, ou le prendre lui-même pour pièce (reçu sans pièce jointe, rendu en PDF). Elle ne fait
// que du N0 (classer, rattacher, affecter, écarter) ; elle ne répète pas le référentiel :
// l'exécutant « gestionnaire » est celui du bail. Fonctions pures.

/** Assez court pour rester univoque, et pour tenir dans le préfixe du tri. */
export const LIMITES_CONSIGNE = {
  sujet: 80,
  quand: 200,
  texte: 400,
  expediteurs: 10,
  objets: 10,
  valeurObjet: 120,
} as const

/** Au-delà, « quand » n'est plus télégraphique : chaque mot coûte à chaque tri. */
const MOTS_QUAND_MAX = 20

const texte = z.string().trim().min(1)

/** Adresse complète (sylvie@chatelhome.fr) ou domaine (chatelhome.fr), en minuscules. */
export const motifExpediteur = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^([^\s@<>]+@)?([a-z0-9-]+\.)+[a-z]{2,}$/,
    'Adresse ou domaine : sylvie@chatelhome.fr ou chatelhome.fr',
  )

// Déclencheur par objet : comparaison sans casse, accents, guillemets ni espaces en trop.

export type ConditionObjet = { operateur: OperateurObjet; valeur: string }

/** L'objet comparable : « Vos documents « Banque » à disposition » → « vos documents banque a disposition ». */
export function normaliserObjet(objet: string): string {
  return objet
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[«»“”„"]/g, ' ')
    .replace(/[’‘`]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

const LIBELLES_OPERATEUR: Record<OperateurObjet, string> = {
  est: 'est',
  contient: 'contient',
  commence_par: 'commence par',
  finit_par: 'finit par',
  ne_contient_pas: 'ne contient pas',
}

/** « ne contient pas » avant « contient » : le plus long d'abord. */
const LECTURE_OPERATEUR = new RegExp(
  `^(${Object.values(LIBELLES_OPERATEUR)
    .sort((a, b) => b.length - a.length)
    .join('|')})(?:\\s*:\\s*|\\s+)(.*)$`,
  'i',
)

/** Une ligne lisible « commence par : Vos documents » ; null si elle ne se lit pas. */
export function analyserConditionObjet(ligne: string): ConditionObjet | null {
  const m = LECTURE_OPERATEUR.exec(ligne.trim())
  if (!m) return null
  const libelle = m[1]!.toLowerCase().replace(/\s+/g, ' ')
  const operateur = OPERATEURS_OBJET.find((o) => LIBELLES_OPERATEUR[o] === libelle)!
  let valeur = m[2]!.trim()
  const englobee = /^"([^"]+)"$/.exec(valeur) ?? /^«\s*([^«»]+?)\s*»$/.exec(valeur)
  if (englobee) valeur = englobee[1]!.trim()
  return valeur ? { operateur, valeur } : null
}

export function ecrireConditionObjet(c: ConditionObjet): string {
  return `${LIBELLES_OPERATEUR[c.operateur]} : ${c.valeur}`
}

const POSITIF = (c: ConditionObjet) => c.operateur !== 'ne_contient_pas'

function conditionVraie(c: ConditionObjet, objet: string): boolean {
  const v = normaliserObjet(c.valeur)
  switch (c.operateur) {
    case 'est':
      return objet === v
    case 'contient':
      return objet.includes(v)
    case 'commence_par':
      return objet.startsWith(v)
    case 'finit_par':
      return objet.endsWith(v)
    case 'ne_contient_pas':
      return !objet.includes(v)
  }
}

/** Les conditions positives : l'une suffit (aucune : tout objet) ; les exclusions : toutes. */
export function objetCorrespond(
  conditions: readonly ConditionObjet[],
  objet: string | null,
): boolean {
  const o = normaliserObjet(objet ?? '')
  const positives = conditions.filter(POSITIF)
  return (
    (positives.length === 0 || positives.some((c) => conditionVraie(c, o))) &&
    conditions.filter((c) => !POSITIF(c)).every((c) => conditionVraie(c, o))
  )
}

/** Opérateur et valeur, ou la ligne lisible « commence par : Vos documents ». */
export const conditionObjet = z.preprocess(
  (v) => (typeof v === 'string' ? (analyserConditionObjet(v) ?? v) : v),
  z.object(
    {
      operateur: z.enum(OPERATEURS_OBJET),
      valeur: texte.max(LIMITES_CONSIGNE.valeurObjet),
    },
    {
      error: `Condition d’objet : ${Object.values(LIBELLES_OPERATEUR).join(', ')}, puis le texte (commence par : Vos documents).`,
    },
  ),
)

const champsConsigne = {
  sujet: texte.max(LIMITES_CONSIGNE.sujet),
  quand: texte.max(LIMITES_CONSIGNE.quand),
  texte: texte.max(LIMITES_CONSIGNE.texte).nullable(),
  expediteurs: z.array(motifExpediteur).max(LIMITES_CONSIGNE.expediteurs),
  objets: z.array(conditionObjet).max(LIMITES_CONSIGNE.objets),
  ignorer: z.boolean(),
  mailVautPiece: z.boolean(),
  regleId: z.uuid().nullable(),
  bailId: z.uuid().nullable(),
  executant: z.enum(EXECUTANTS_CONSIGNE),
  executantTiersId: z.uuid().nullable(),
  attenteTiers: z.boolean(),
  delaiJours: z.number().int().min(1).max(365).nullable(),
}

type FormeConsigne = {
  executant?: ExecutantConsigne
  executantTiersId?: string | null
  attenteTiers?: boolean
  ignorer?: boolean
  mailVautPiece?: boolean
  expediteurs?: readonly string[]
  objets?: readonly ConditionObjet[]
  regleId?: string | null
  bailId?: string | null
  delaiJours?: number | null
}
const tiersDesigne = (c: FormeConsigne) => c.executant !== 'tiers' || Boolean(c.executantTiersId)
const attenteAvecTiers = (c: FormeConsigne) =>
  !c.attenteTiers || (c.executant !== undefined && c.executant !== 'responsables')

/** Un déclencheur sûr : un expéditeur, ou une condition d'objet positive. */
export function aDeclencheur(c: {
  expediteurs: readonly string[]
  objets: readonly ConditionObjet[]
}): boolean {
  return c.expediteurs.length > 0 || c.objets.some(POSITIF)
}

/** Ignorer : sans modèle, donc avec un déclencheur sûr, et sans rien créer. */
const ignorerSur = (c: FormeConsigne) =>
  !c.ignorer || aDeclencheur({ expediteurs: c.expediteurs ?? [], objets: c.objets ?? [] })
const ignorerSansEffet = (c: FormeConsigne) =>
  !c.ignorer ||
  (!c.regleId &&
    !c.bailId &&
    (c.executant ?? 'responsables') === 'responsables' &&
    !c.attenteTiers &&
    !c.delaiJours)

/**
 * Le mail vaut pièce : sans modèle, d'un expéditeur désigné (l'objet seul ne suffit pas,
 * n'importe qui pourrait l'écrire), et jamais avec ignorer.
 */
const vautPieceSur = (c: FormeConsigne) => !c.mailVautPiece || (c.expediteurs ?? []).length > 0
const vautPieceSansIgnorer = (c: FormeConsigne) => !(c.mailVautPiece && c.ignorer)

const MESSAGE_VAUT_PIECE_SUR =
  'Le mail ne vaut pièce que venant d’un expéditeur désigné (adresse ou domaine) : l’objet seul ne suffit pas.'
const MESSAGE_VAUT_PIECE_IGNORER =
  'Une consigne qui ignore ne fait entrer aucune pièce : ignorer ou prendre le mail pour pièce, pas les deux.'

const MESSAGE_IGNORER_SUR =
  'Ignorer se décide sans modèle : un expéditeur ou une condition d’objet (est, contient, commence par, finit par).'
const MESSAGE_IGNORER_SANS_EFFET =
  'Une consigne qui ignore ne crée rien : ni règle, ni bail, ni exécutant, ni attente, ni délai.'

export const consigneCreation = z
  .object({
    entiteId: z.uuid().nullable(),
    sujet: champsConsigne.sujet,
    quand: champsConsigne.quand,
    texte: champsConsigne.texte.optional().default(null),
    expediteurs: champsConsigne.expediteurs.default([]),
    objets: champsConsigne.objets.default([]),
    ignorer: champsConsigne.ignorer.default(false),
    mailVautPiece: champsConsigne.mailVautPiece.default(false),
    regleId: champsConsigne.regleId.optional().default(null),
    bailId: champsConsigne.bailId.optional().default(null),
    executant: champsConsigne.executant.default('responsables'),
    executantTiersId: champsConsigne.executantTiersId.optional().default(null),
    attenteTiers: champsConsigne.attenteTiers.default(false),
    delaiJours: champsConsigne.delaiJours.optional().default(null),
  })
  .refine(tiersDesigne, {
    message: 'Exécutant « tiers » : désigner le tiers.',
    path: ['executantTiersId'],
  })
  .refine(attenteAvecTiers, {
    message: 'Attendre un tiers suppose un exécutant tiers (gestionnaire ou tiers désigné).',
    path: ['attenteTiers'],
  })
  .refine(ignorerSur, { message: MESSAGE_IGNORER_SUR, path: ['ignorer'] })
  .refine(ignorerSansEffet, { message: MESSAGE_IGNORER_SANS_EFFET, path: ['ignorer'] })
  .refine(vautPieceSur, { message: MESSAGE_VAUT_PIECE_SUR, path: ['mailVautPiece'] })
  .refine(vautPieceSansIgnorer, { message: MESSAGE_VAUT_PIECE_IGNORER, path: ['mailVautPiece'] })

/** La forme complète d'une consigne, après modification : mêmes exigences qu'à la création. */
export function erreursForme(c: FormeConsigne): string[] {
  return [
    !tiersDesigne(c) && 'Exécutant « tiers » : désigner le tiers.',
    !attenteAvecTiers(c) &&
      'Attendre un tiers suppose un exécutant tiers (gestionnaire ou tiers désigné).',
    !ignorerSur(c) && MESSAGE_IGNORER_SUR,
    !ignorerSansEffet(c) && MESSAGE_IGNORER_SANS_EFFET,
    !vautPieceSur(c) && MESSAGE_VAUT_PIECE_SUR,
    !vautPieceSansIgnorer(c) && MESSAGE_VAUT_PIECE_IGNORER,
  ].filter((m): m is string => Boolean(m))
}

/** Modification : l'entité ne change pas (désactiver, puis en créer une autre). */
export const consigneModification = z.object(champsConsigne).partial()

/** Activer : un humain valide une consigne proposée ; désactiver : la mettre de côté. */
export const actionConsigne = z.object({ action: z.enum(['activer', 'desactiver']) })

/** Proposer une consigne depuis une tâche : ce que l'humain précise, facultatif. */
export const demandeConsigne = z.object({ demande: texte.max(1000).optional() })

export type ConsigneCreation = z.infer<typeof consigneCreation>
export type ConsigneModification = z.infer<typeof consigneModification>

/** Ce que la grille et le tri savent d'une consigne. */
export type ConsigneEvaluee = {
  id: string
  entiteId: string | null
  sujet: string
  quand: string
  texte: string | null
  expediteurs: readonly string[]
  objets: readonly ConditionObjet[]
  /** Mail déclenché : écarté comme sans rapport, sans appel au modèle. */
  ignorer: boolean
  /** Mail déclenché sans pièce jointe : son corps, rendu en PDF, entre comme pièce. */
  mailVautPiece: boolean
  regleId: string | null
  bailId: string | null
  executant: ExecutantConsigne
  executantTiersId: string | null
  attenteTiers: boolean
  delaiJours: number | null
}

// Déclencheurs par expéditeur et par objet : la part sûre du tri, sans modèle.

/** L'adresse d'un en-tête « Nom <adresse> » (ou l'adresse seule), en minuscules. */
export function adresseDe(de: string | null): string | null {
  if (!de) return null
  const m = /<([^<>\s]+@[^<>\s]+)>/.exec(de) ?? /([^\s<>"]+@[^\s<>"]+)/.exec(de)
  return m ? m[1]!.toLowerCase() : null
}

/** Une adresse complète : égalité ; un domaine : ce domaine ou un de ses sous-domaines. */
export function expediteurCorrespond(motif: string, de: string | null): boolean {
  const adresse = adresseDe(de)
  if (!adresse) return false
  if (motif.includes('@')) return adresse === motif
  const domaine = adresse.slice(adresse.lastIndexOf('@') + 1)
  return domaine === motif || domaine.endsWith(`.${motif}`)
}

export type MailDeclencheur = { de: string | null; objet: string | null }

/**
 * Déclenchée : au moins un déclencheur sûr, et chacun de ceux qu'elle pose correspond
 * (l'un de ses expéditeurs ET ses conditions d'objet).
 */
export function consigneDeclenchee(
  c: Pick<ConsigneEvaluee, 'expediteurs' | 'objets'>,
  mail: MailDeclencheur,
): boolean {
  return (
    aDeclencheur(c) &&
    (c.expediteurs.length === 0 || c.expediteurs.some((m) => expediteurCorrespond(m, mail.de))) &&
    objetCorrespond(c.objets, mail.objet)
  )
}

export function consignesDeclenchees(
  consignes: readonly Pick<ConsigneEvaluee, 'id' | 'expediteurs' | 'objets'>[],
  mail: MailDeclencheur,
): string[] {
  return consignes.filter((c) => consigneDeclenchee(c, mail)).map((c) => c.id)
}

/**
 * Une consigne « ignorer » déclenchée : le mail est écarté, sans modèle. Elle l'emporte
 * sur les autres (plus étroite, voulue explicitement) ; la réponse attendue d'un tiers
 * passe avant elle (le service la reconnaît d'abord).
 */
export function consigneIgnorante(
  consignes: readonly Pick<ConsigneEvaluee, 'id' | 'expediteurs' | 'objets' | 'ignorer'>[],
  mail: MailDeclencheur,
): string | null {
  return consignes.find((c) => c.ignorer && consigneDeclenchee(c, mail))?.id ?? null
}

/**
 * La consigne retenue pour un mail. Déclenchée par son expéditeur ou son objet et seule :
 * elle l'emporte (sûr). Plusieurs : Claude départage parmi elles, sinon aucune. Aucune :
 * le choix de Claude d'après « quand », s'il désigne une consigne proposée au tri.
 */
export function choisirConsigne(e: {
  declenchees: readonly string[]
  choixClaude: string | null
  candidates: readonly string[]
}): { consigneId: string; deterministe: boolean } | null {
  if (e.declenchees.length === 1) return { consigneId: e.declenchees[0]!, deterministe: true }
  if (e.declenchees.length > 1)
    return e.choixClaude && e.declenchees.includes(e.choixClaude)
      ? { consigneId: e.choixClaude, deterministe: true }
      : null
  if (e.choixClaude && e.candidates.includes(e.choixClaude))
    return { consigneId: e.choixClaude, deterministe: false }
  return null
}

// Grille : une consigne courte, univoque, cohérente avec le référentiel.

export type ContexteEvaluation = {
  regle: { id: string; entiteId: string; bailId: string | null } | null
  bail: { id: string; entiteId: string; gestionnaireId: string | null } | null
  /** Bail de la règle, quand la consigne n'en désigne pas. */
  bailDeRegle: { gestionnaireId: string | null } | null
  /** Les autres consignes non désactivées visibles (pour les chevauchements). */
  autres: readonly {
    id: string
    sujet: string
    expediteurs: readonly string[]
    objets: readonly ConditionObjet[]
  }[]
}

export type Evaluation = { erreurs: string[]; avertissements: string[] }

export const GESTES_N2 =
  /\b(payer|paie[rz]?|r[eé]gler|virement|pr[eé]l[eè]vement à faire|d[eé]clarer|d[eé]clare[rz]?|signer|signe[rz]?)\b/i

/** Mêmes conditions d'objet, à l'ordre et à la casse près. */
function memesObjets(a: readonly ConditionObjet[], b: readonly ConditionObjet[]): boolean {
  const cle = (l: readonly ConditionObjet[]) =>
    l
      .map((c) => `${c.operateur}:${normaliserObjet(c.valeur)}`)
      .sort()
      .join('|')
  return cle(a) === cle(b)
}

/** Deux motifs d'expéditeur se recouvrent : l'un attrape les mails de l'autre. */
function recouvrement(a: string, b: string): boolean {
  const exemple = (m: string) => (m.includes('@') ? m : `x@${m}`)
  return expediteurCorrespond(a, exemple(b)) || expediteurCorrespond(b, exemple(a))
}

/**
 * Grille de qualité. Les erreurs empêchent d'écrire la consigne ; les avertissements
 * guident sa rédaction (l'agent les montre à l'humain avant de proposer).
 */
export function evaluerConsigne(c: ConsigneEvaluee, ctx: ContexteEvaluation): Evaluation {
  const erreurs: string[] = []
  const avertissements: string[] = []

  erreurs.push(...erreursForme(c))
  if (c.entiteId === null) {
    if (c.regleId || c.bailId || c.executant !== 'responsables' || c.attenteTiers)
      erreurs.push(
        'Une consigne sans entité ne fait que guider le tri : ni règle, ni bail, ni exécutant.',
      )
  } else {
    if (ctx.regle && ctx.regle.entiteId !== c.entiteId)
      erreurs.push('La règle désignée relève d’une autre entité que la consigne.')
    if (ctx.bail && ctx.bail.entiteId !== c.entiteId)
      erreurs.push('Le bail désigné relève d’une autre entité que la consigne.')
  }
  if (ctx.regle?.bailId && ctx.bail && ctx.regle.bailId !== ctx.bail.id)
    erreurs.push('Le bail désigné n’est pas le bail de la règle : n’en garder qu’un.')

  const gestionnaire = ctx.bail?.gestionnaireId ?? ctx.bailDeRegle?.gestionnaireId ?? null
  if (c.executant === 'gestionnaire' && !gestionnaire)
    erreurs.push(
      'Exécutant « gestionnaire » : le bail (de la consigne ou de la règle) doit avoir un gestionnaire.',
    )
  if (c.executant === 'tiers' && gestionnaire && c.executantTiersId !== gestionnaire)
    erreurs.push(
      'Le bail désigne un autre gestionnaire : choisir l’exécutant « gestionnaire » plutôt que de le contredire.',
    )

  if (!aDeclencheur(c))
    avertissements.push(
      'Sans expéditeur ni condition d’objet, seul Claude reconnaîtra le mail d’après « quand » : une adresse, un domaine ou un objet rend le tri sûr.',
    )
  const mots = c.quand.split(/\s+/).filter(Boolean)
  if (mots.filter((m) => m.length > 2).length < 4)
    avertissements.push(
      '« quand » très court : décrire le mail attendu (organisme, objet, ce qui est demandé).',
    )
  if (mots.length > MOTS_QUAND_MAX)
    avertissements.push(
      `« quand » long (${mots.length} mots) : style télégraphique, sans articles ni tournures (relu à chaque tri).`,
    )
  if (c.ignorer && c.texte)
    avertissements.push(
      'Précisions jamais lues : une consigne qui ignore se décide sans modèle, sur ses déclencheurs.',
    )
  if (GESTES_N2.test(`${c.quand} ${c.texte ?? ''}`))
    avertissements.push(
      'Payer, déclarer et signer restent humains : la consigne ne fera que classer et affecter.',
    )
  for (const autre of ctx.autres) {
    if (autre.id === c.id) continue
    if (
      c.expediteurs.some((m) => autre.expediteurs.some((n) => recouvrement(m, n))) &&
      memesObjets(c.objets, autre.objets)
    )
      avertissements.push(
        `Même expéditeur que la consigne « ${autre.sujet} » : préciser « quand » pour les départager, ou les fusionner.`,
      )
  }
  return { erreurs, avertissements }
}

// Effets d'une consigne sur le mail retenu.

export type PlanConsigne = {
  cible:
    | { type: 'nouvelle'; echeanceId: string | null }
    | {
        type: 'tache_existante'
        tacheId: string
        evenement: 'attendre_tiers' | null
        /** Confier au tiers, si la tâche n'a encore personne. */
        affecterTiers: string | null
      }
  executantTiersId: string | null
  attendre: boolean
  dateDue: string | null
  avertissements: string[]
}

/**
 * Ce que fait la consigne. Avec une règle : le mail rejoint l'échéance ouverte, et sa tâche
 * ouverte s'il y en a une (mise en attente, confiée au tiers si personne ne l'a), sinon une
 * nouvelle tâche rattachée à l'échéance. Sans règle : une nouvelle tâche. L'exécutant
 * « gestionnaire » est résolu par le service depuis le bail.
 */
export function planConsigne(e: {
  consigne: Pick<
    ConsigneEvaluee,
    'regleId' | 'executant' | 'executantTiersId' | 'attenteTiers' | 'delaiJours'
  >
  gestionnaireId: string | null
  echeance: { id: string } | null
  tacheEcheance: { id: string; statut: StatutTache; sansExecutant: boolean } | null
  dateLimite: string | null
  jourReception: string
}): PlanConsigne {
  const c = e.consigne
  const avertissements: string[] = []
  let executantTiersId: string | null = null
  if (c.executant === 'tiers') executantTiersId = c.executantTiersId
  if (c.executant === 'gestionnaire') {
    executantTiersId = e.gestionnaireId
    if (!e.gestionnaireId)
      avertissements.push('Bail sans gestionnaire : la tâche reste chez les responsables.')
  }
  const attendre = c.attenteTiers && executantTiersId !== null
  const dateDue =
    e.dateLimite ?? (c.delaiJours ? ajouterJours(e.jourReception, c.delaiJours) : null)

  if (e.echeance && e.tacheEcheance) {
    const t = e.tacheEcheance
    return {
      cible: {
        type: 'tache_existante',
        tacheId: t.id,
        evenement:
          attendre && (t.statut === 'a_faire' || t.statut === 'en_cours') ? 'attendre_tiers' : null,
        affecterTiers: t.sansExecutant ? executantTiersId : null,
      },
      executantTiersId,
      attendre,
      dateDue,
      avertissements,
    }
  }
  return {
    cible: { type: 'nouvelle', echeanceId: e.echeance?.id ?? null },
    executantTiersId,
    attendre,
    dateDue,
    avertissements,
  }
}
